import {
  FloatingArrow,
  arrow,
  autoUpdate,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
  useTransitionStyles,
} from "@floating-ui/react";
import { type ReactNode, useRef, useState } from "react";
import { createPortal } from "react-dom";
import module from "./Tooltip.module.scss";

type Placement =
  | "top"
  | "top-start"
  | "top-end"
  | "right"
  | "right-start"
  | "right-end"
  | "bottom"
  | "bottom-start"
  | "bottom-end"
  | "left"
  | "left-start"
  | "left-end";

export const Tooltip = ({
  children,
  tooltip,
  hideArrow,
  placement,
  customOffset = 10,
  customTransform,
  maxWidth,
  position,
  delay = 0,
  forceOpen,
  index,
  className = "",
}: {
  children: ReactNode | ReactNode[];
  tooltip: ReactNode | string;
  placement?: Placement;
  hideArrow?: boolean;
  customOffset?: number;
  maxWidth?: number;
  position?: "absolute" | "fixed";
  customTransform?: number;
  delay?: number;
  index?: number;
  forceOpen?: boolean;
  className?: string;
}) => {
  "use no memo";
  const [isOpen, setIsOpen] = useState(false);
  const arrowRef = useRef(null);

  const { refs, floatingStyles, context, y, x } = useFloating({
    open: isOpen || !!forceOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    strategy: position || "fixed",
    transform: true,
    placement: placement ?? "top",
    middleware: [
      offset(customOffset || 4),
      flip({
        fallbackAxisSideDirection: "start",
      }),
      shift({ padding: (customTransform || 0) + 5 }),
      arrow({
        element: arrowRef,
      }),
    ],
  });

  const { isMounted, styles } = useTransitionStyles(context);

  const hover = useHover(context, {
    move: false,
    delay: { open: delay },
  });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "tooltip" });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role,
  ]);

  return (
    <div className="tooltip-WsA9Pt7X1Xr2kTA">
      <div
        ref={refs.setReference}
        {...getReferenceProps()}
        className={`${module.trigger} trigger-WsA9Pt7X1Xr2kTA`}
      >
        {children}
      </div>
      {isMounted &&
        tooltip &&
        createPortal(
          <div
            style={{ ...styles, position: "relative", zIndex: index || 99999 }}
            className={className}
          >
            <div
              ref={refs.setFloating}
              style={{
                ...floatingStyles,
                maxWidth: maxWidth || 200,
                transform: `translate(${x}px, ${y + (customTransform || 0)}px)`,
              }}
              {...getFloatingProps()}
              className={`WsA9Pt7X1Xr2kTA-content ${module.content}`}
            >
              {!hideArrow && (
                <FloatingArrow
                  ref={arrowRef}
                  context={context}
                  fill="#12033e"
                  tipRadius={2}
                />
              )}
              {typeof tooltip === "string" ? <span>{tooltip}</span> : tooltip}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};
